// PRE RMA — Stockage local CPN (IndexedDB), même principe que offline/db.ts
// pour le RCE (point 28 : la saisie ET la modification doivent continuer à
// fonctionner sans Internet). Base IndexedDB séparée (pre_rma_csb_cpn) —
// module ajouté après les Phases 1-8, on ne touche pas à la base existante
// du RCE pour ne rien risquer sur elle.

const DB_NAME = 'pre_rma_csb_cpn';
const DB_VERSION = 2;
const STORE_QUEUE = 'cpn';
const STORE_CACHE = 'cpn_cache';
const STORE_TOTAUX = 'totaux_cpn';

export type SyncStatus = 'PENDING' | 'SYNCED' | 'ERROR' | 'CONFLICT';
export type Operation = 'CREATE' | 'UPDATE' | 'DELETE';

export interface LocalCpn {
  localId: string;
  operation: Operation;
  periodeId: string;
  csbId: string;
  cpnId?: string;         // id serveur — requis pour UPDATE et DELETE
  baseVersion?: number;   // version de depart — requis pour UPDATE et DELETE
  payload: Record<string, unknown>;
  syncStatus: SyncStatus;
  serverId?: string;
  serverVersion?: number;
  erreur?: string;
  versionServeurConflit?: number;
  codeHttp?: number;      // code HTTP du refus définitif (400, 422, 500…)
  refuseLe?: number;
  createdAt: number;
  updatedAt: number;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_QUEUE)) {
        const store = db.createObjectStore(STORE_QUEUE, { keyPath: 'localId' });
        store.createIndex('by_sync_status', 'syncStatus');
        store.createIndex('by_periode', 'periodeId');
      }
      if (!db.objectStoreNames.contains(STORE_CACHE)) {
        const cache = db.createObjectStore(STORE_CACHE, { keyPath: 'id' });
        cache.createIndex('by_periode', 'periodeId');
      }
      // v2 : derniers totaux CPN calculés localement, un enregistrement par période.
      if (!db.objectStoreNames.contains(STORE_TOTAUX)) {
        db.createObjectStore(STORE_TOTAUX, { keyPath: 'periodeId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Une opération, une transaction. Le résultat n'est rendu qu'APRÈS la validation (commit) de la transaction : quand `await` rend la main,
 * la donnée est réellement écrite sur l'appareil (avant : on rendait la main dès la réussite de la requête, avant le commit).
 */
async function withStore<T>(storeName: string, mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    let resultat: T;
    let tx: IDBTransaction;
    try {
      tx = db.transaction(storeName, mode);
      const req = fn(tx.objectStore(storeName));
      req.onsuccess = () => { resultat = req.result; };
      req.onerror = () => { db.close(); reject(req.error); };
    } catch (err) { db.close(); reject(err); return; }
    tx.oncomplete = () => { db.close(); resolve(resultat); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Opération locale annulée')); };
  });
}

export async function sauvegarderLocalement(item: LocalCpn): Promise<void> {
  await withStore(STORE_QUEUE, 'readwrite', (store) => store.put(item));
}

/** Écrit TOUT un lot dans la file locale en UNE transaction : tout est enregistré, ou rien (import massif, 100 % hors connexion). */
export async function sauvegarderLotLocalement(items: LocalCpn[]): Promise<void> {
  if (!items.length) return;
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, 'readwrite');
    const store = tx.objectStore(STORE_QUEUE);
    for (const item of items) store.put(item);
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Écriture locale annulée')); };
  });
}

/** Toute la file CPN d'une période (en attente, refusées, en conflit ET déjà envoyées) : sert à fusionner avec la liste du serveur. */
export async function listerParPeriode(periodeId: string): Promise<LocalCpn[]> {
  const all = await withStore<LocalCpn[]>(STORE_QUEUE, 'readonly', (store) => store.getAll());
  return all.filter((i) => i.periodeId === periodeId);
}

export async function listerEnAttente(): Promise<LocalCpn[]> {
  const all = await withStore<LocalCpn[]>(STORE_QUEUE, 'readonly', (store) => store.getAll());
  return all.filter((i) => i.syncStatus !== 'SYNCED');
}

export async function obtenirEnAttente(localId: string): Promise<LocalCpn | undefined> {
  return withStore<LocalCpn>(STORE_QUEUE, 'readonly', (store) => store.get(localId));
}

export async function marquerSynchronise(localId: string, serverId: string, serverVersion: number): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  await sauvegarderLocalement({ ...item, syncStatus: 'SYNCED', serverId, serverVersion, updatedAt: Date.now() });
}

export async function marquerErreur(localId: string, erreur: string): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  await sauvegarderLocalement({ ...item, syncStatus: 'ERROR', erreur, updatedAt: Date.now() });
}

/** Refus définitif du serveur (400/422… ou crash 500) : statut ERROR « Refusé / À vérifier », la file passe à la saisie suivante. */
export async function marquerRefuse(localId: string, motif: string, codeHttp?: number): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  const entete = `Refusé${codeHttp ? ` (code ${codeHttp})` : ''} : `;
  await sauvegarderLocalement({ ...item, syncStatus: 'ERROR', erreur: entete + motif, codeHttp, refuseLe: Date.now(), updatedAt: Date.now() });
}

/** Point 30 (même décision que le RCE) : jamais d'écrasement silencieux — le serveur gagne. */
export async function marquerConflit(localId: string, versionServeur: number, message: string): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  await sauvegarderLocalement({ ...item, syncStatus: 'CONFLICT', versionServeurConflit: versionServeur, erreur: message, updatedAt: Date.now() });
}

export async function supprimerLocal(localId: string): Promise<void> {
  await withStore(STORE_QUEUE, 'readwrite', (store) => store.delete(localId));
}

/** Retire une saisie du cache de lecture (suppression locale immédiate, confirmée ou en attente d'envoi). */
export async function supprimerDuCacheCpn(cpnId: string): Promise<void> {
  await withStore(STORE_CACHE, 'readwrite', (store) => store.delete(cpnId));
}

/**
 * Purge de la file les éléments DÉJÀ ENVOYÉS (SYNCED) qui concernent une saisie supprimée (sa création, ses modifications) :
 * sans cela, la création SYNCED ferait réapparaître la femme dans la liste après la suppression.
 */
export async function purgerElementsSynchronisesCpn(cpnId: string): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_QUEUE, 'readwrite');
    const store = tx.objectStore(STORE_QUEUE);
    const lecture = store.getAll();
    lecture.onsuccess = () => {
      for (const i of lecture.result as LocalCpn[]) {
        if (i.syncStatus === 'SYNCED' && (i.serverId === cpnId || i.cpnId === cpnId)) store.delete(i.localId);
      }
    };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Purge locale annulée')); };
  });
}

export async function mettreEnCacheCpn(periodeId: string, lignes: Array<Record<string, unknown> & { id: string }>): Promise<void> {
  const db = await openDb();
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(STORE_CACHE, 'readwrite');
    const store = tx.objectStore(STORE_CACHE);
    const presents = new Set(lignes.map((l) => l.id));
    // La liste reçue est COMPLÈTE pour la période : une saisie absente a été supprimée (autre appareil) → retirée du cache,
    // sinon elle réapparaîtrait (et fausserait les totaux) hors connexion.
    const lecture = store.getAll();
    lecture.onsuccess = () => {
      for (const c of lecture.result as Array<{ id: string; periodeId?: string }>) {
        if (c.periodeId === periodeId && !presents.has(c.id)) store.delete(c.id);
      }
      for (const l of lignes) store.put({ ...l, periodeId });
    };
    tx.oncomplete = () => { db.close(); resolve(); };
    tx.onerror = () => { db.close(); reject(tx.error); };
    tx.onabort = () => { db.close(); reject(tx.error ?? new Error('Mise en cache annulée')); };
  });
}

export async function chargerCpnCache(periodeId: string): Promise<Array<Record<string, unknown>>> {
  const all = await withStore<Array<Record<string, unknown> & { periodeId: string }>>(STORE_CACHE, 'readonly', (store) => store.getAll());
  return all.filter((c) => c.periodeId === periodeId);
}

/** Modifie le contenu d'une création encore en file (avant envoi) : corrige une saisie faite hors connexion. */
export async function remplacerPayloadEnFile(localId: string, payload: Record<string, unknown>): Promise<void> {
  const item = await obtenirEnAttente(localId);
  if (!item) return;
  await sauvegarderLocalement({ ...item, payload: { ...payload, periodeId: item.periodeId, localId }, syncStatus: 'PENDING', erreur: undefined, updatedAt: Date.now() });
}

// --- Totaux CPN calculés localement ---
export interface TotauxCpnStockes { periodeId: string; csbId: string; totaux: Record<string, unknown> }

export async function enregistrerTotauxCpnLocaux(periodeId: string, csbId: string, totaux: Record<string, unknown>): Promise<void> {
  await withStore(STORE_TOTAUX, 'readwrite', (store) => store.put({ periodeId, csbId, totaux } satisfies TotauxCpnStockes));
}

export async function lireTotauxCpnLocaux(periodeId: string): Promise<TotauxCpnStockes | undefined> {
  return withStore<TotauxCpnStockes | undefined>(STORE_TOTAUX, 'readonly', (store) => store.get(periodeId));
}
